"use client"

import * as React from "react"

/** How long (ms) after the first load the page keeps correcting the scroll position. */
const SETTLE_MS = 4000

/**
 * Keeps a `/#section` link on its target during the first load.
 *
 * The browser scrolls to the hash once, but images and carousels above the
 * target then load and shift the layout, leaving the target far off screen.
 * This re-aligns to the hash whenever the page height changes during the
 * first few seconds, and stops as soon as the visitor scrolls or presses a key
 * so it never fights them. The target's `scroll-m-20` keeps it under the
 * sticky header. Renders nothing.
 */
export function HashScroll() {
    React.useEffect(() => {
        const hash = window.location.hash
        if (!hash || hash.length < 2) return
        let id = hash.slice(1)
        try {
            id = decodeURIComponent(id)
        } catch {
            /* keep the raw value */
        }

        let done = false
        const align = () => {
            if (done) return
            document.getElementById(id)?.scrollIntoView({ behavior: "instant" })
        }
        const stop = () => {
            done = true
            observer.disconnect()
            window.clearTimeout(timer)
            for (const ev of ["wheel", "touchstart", "keydown", "mousedown"]) {
                window.removeEventListener(ev, stop)
            }
            window.removeEventListener("load", align)
        }

        const observer = new ResizeObserver(align)
        observer.observe(document.body)
        const timer = window.setTimeout(stop, SETTLE_MS)
        for (const ev of ["wheel", "touchstart", "keydown", "mousedown"]) {
            window.addEventListener(ev, stop, { passive: true })
        }
        window.addEventListener("load", align)
        align()
        return stop
    }, [])

    return null
}
